import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Avatar,
    Collapse,
    Divider,
    List,
    ListItemAvatar,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography
} from "@mui/material";

import HomeIcon from "@mui/icons-material/Home";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import SavingsIcon from "@mui/icons-material/Savings";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CalculateIcon from "@mui/icons-material/Calculate";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import HelpIcon from "@mui/icons-material/Help";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import LogoutIcon from "@mui/icons-material/Logout";
import BusinessIcon from "@mui/icons-material/Business";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PaymentsIcon from "@mui/icons-material/Payments";
import ExtensionIcon from "@mui/icons-material/Extension";
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';

import Search from "../../Search/Search";
import ContentViewPage from "../../../Types/ContentViewPage"; // (kept import if you use it elsewhere)
import { AuthContext } from "../../../Types/AuthContext";
import { useNavigate } from "react-router-dom";

import TipCounterLogo from '../../../Assets/Logo/TipCounterLogo_Black.png';

interface SideMenuProps {}

const ACCENT = "#c79656";
const HOVER_BG = "rgba(199,150,86,0.08)";
const HIGHLIGHT_BG = "rgba(199,150,86,0.14)";
const CARD_SHADOW = "0 6px 20px rgba(0,0,0,0.06)";
const FOCUS_OUTLINE = "rgba(199,150,86,0.45)";

export default function SideMenu(_: SideMenuProps) {
    const navigate = useNavigate();
    const auth = useContext(AuthContext);

    const [countingOpen, setCountingOpen] = useState(false);
    const [accountOpen, setAccountOpen] = useState(false);
    const [orgOpen, setOrgOpen] = useState(true);
    const [financeOpen, setFinanceOpen] = useState(false);
    const [filter, setFilter] = useState("");

    const accountFooterRef = useRef<HTMLDivElement>(null);

    // Close account panel when clicking outside
    useEffect(() => {
        const handleDocumentClick = (e: MouseEvent) => {
            if (!accountOpen) return;
            const root = accountFooterRef.current;
            if (root && !root.contains(e.target as Node)) {
                setAccountOpen(false);
            }
        };
        document.addEventListener("mousedown", handleDocumentClick);
        return () => document.removeEventListener("mousedown", handleDocumentClick);
    }, [accountOpen]);

    const matchesFilter = (label: string) => {
        const q = filter.trim().toLowerCase();
        return q.length > 0 && label.toLowerCase().includes(q);
    };

    const itemSx = (nested = false, highlight = false) =>
        ({
            mx: 1,
            my: 0.25,
            borderRadius: 12,
            pl: nested ? 3.5 : 2,
            pr: 1.25,
            py: 0.75,
            transition: "background 160ms ease, transform 160ms ease, box-shadow 160ms ease, color 160ms ease",
            background: highlight ? HIGHLIGHT_BG : "transparent",
            "&:hover": {
                background: HOVER_BG,
                transform: "translateX(2px)",
                boxShadow: "0 4px 14px rgba(0,0,0,0.05)"
            },
            "&.Mui-focusVisible": {
                outline: `2px solid ${FOCUS_OUTLINE}`,
                outlineOffset: 2
            },
            ...(nested ? { "&:hover .MuiListItemIcon-root": { color: ACCENT } } : {})
        } as const);

    const sectionHeader = (
        label: string,
        icon: React.ReactNode,
        open: boolean,
        onClick: () => void,
        controlsId: string
    ) => (
        <ListItemButton
            onClick={onClick}
            aria-expanded={open}
            aria-controls={controlsId}
            sx={{
                ...itemSx(false, false),
                mt: 1,
                "& .MuiListItemIcon-root": { color: ACCENT },
                "& .MuiSvgIcon-root": { fontSize: 22 }
            }}
        >
            <ListItemIcon sx={{ minWidth: 36 }}>{icon}</ListItemIcon>
            <ListItemText
                primary={label}
                slotProps={{
                    primary: {
                        component: "span",
                        style: { fontSize: 12, fontWeight: 700, letterSpacing: 0.2 }
                    }
                }}
            />
            {open ? <ExpandLess sx={{ color: "text.secondary" }} /> : <ExpandMore sx={{ color: "text.secondary" }} />}
        </ListItemButton>
    );

    const organizationMenu = () => {
        const teamHL = matchesFilter("Team");
        const rolesHL = matchesFilter("Roles & Permissions");
        const locHL = matchesFilter("Locations");

        return (
            <List component="nav" aria-label="Organization" dense>
                {sectionHeader("Organization", <BusinessIcon />, orgOpen, () => setOrgOpen(!orgOpen), "nav-org-collapse")}

                <Collapse in={orgOpen} timeout="auto" unmountOnExit id="nav-org-collapse">
                    <List component="div" disablePadding dense>
                        <ListItemButton sx={itemSx(true, teamHL)} onClick={() => navigate("/organisation/team")}>
                            <ListItemIcon
                                sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: teamHL ? ACCENT : "text.secondary" }}
                            >
                                <PeopleAltIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Team"
                                slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                            />
                        </ListItemButton>

                        <ListItemButton sx={itemSx(true, rolesHL)} onClick={() => navigate("/organisation/permissions")}>
                            <ListItemIcon
                                sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: rolesHL ? ACCENT : "text.secondary" }}
                            >
                                <AdminPanelSettingsIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Roles & Permissions"
                                slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                            />
                        </ListItemButton>

                        <ListItemButton sx={itemSx(true, locHL)} onClick={() => navigate("/organisation/locations")}>
                            <ListItemIcon
                                sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: locHL ? ACCENT : "text.secondary" }}
                            >
                                <LocationCityIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Locations"
                                slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                            />
                        </ListItemButton>
                    </List>
                </Collapse>
            </List>
        );
    };

    const financeMenu = () => {
        const tipHL = matchesFilter("Tip Jar");
        const payoutsHL = matchesFilter("Payouts");

        return (
            <List component="nav" aria-label="Finance" dense>
                {sectionHeader("Finance", <SavingsIcon />, financeOpen, () => setFinanceOpen(!financeOpen), "nav-finance-collapse")}

                <Collapse in={financeOpen} timeout="auto" unmountOnExit id="nav-finance-collapse">
                    <List component="div" disablePadding dense>
                        <ListItemButton sx={itemSx(true, tipHL)} onClick={() => navigate("/finance/tipjar")}>
                            <ListItemIcon
                                sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: tipHL ? ACCENT : "text.secondary" }}
                            >
                                <SavingsIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Tip Jar"
                                slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                            />
                        </ListItemButton>

                        <ListItemButton sx={itemSx(true, payoutsHL)} onClick={() => navigate("/finance/payouts")}>
                            <ListItemIcon
                                sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: payoutsHL ? ACCENT : "text.secondary" }}
                            >
                                <PaymentsIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Payouts"
                                slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                            />
                        </ListItemButton>

                        <ListItemButton sx={itemSx(true, payoutsHL)} onClick={() => navigate("/finance/countperiods")}>
                            <ListItemIcon
                                sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: payoutsHL ? ACCENT : "text.secondary" }}
                            >
                                <ViewTimelineIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Manage count periods"
                                slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                            />
                        </ListItemButton>
                    </List>
                </Collapse>
            </List>
        );
    };

    const countingMenu = () => {
        const manualHL = matchesFilter("Manual Count");
        const autoHL = matchesFilter("Auto Count");
        const importHL = matchesFilter("Import CSV");

        return (
            <List component="nav" aria-label="Counting" dense>
                {sectionHeader("Counting", <CalculateIcon />, countingOpen, () => setCountingOpen(!countingOpen), "nav-counting-collapse")}

                <Collapse in={countingOpen} timeout="auto" unmountOnExit id="nav-counting-collapse">
                    <List component="div" disablePadding dense>
                        <ListItemButton sx={itemSx(true, manualHL)} onClick={() => navigate("/counting/manual")}>
                            <ListItemIcon
                                sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: manualHL ? ACCENT : "text.secondary" }}
                            >
                                <CalculateIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Manual Count"
                                slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                            />
                        </ListItemButton>

                        <ListItemButton sx={itemSx(true, autoHL)} onClick={() => navigate("/counting/auto")}>
                            <ListItemIcon
                                sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: autoHL ? ACCENT : "text.secondary" }}
                            >
                                <AutoAwesomeIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Auto Count"
                                slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                            />
                        </ListItemButton>

                        <ListItemButton sx={itemSx(true, importHL)} onClick={() => navigate("/counting/import")}>
                            <ListItemIcon
                                sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: importHL ? ACCENT : "text.secondary" }}
                            >
                                <UploadFileIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Import CSV"
                                slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                            />
                        </ListItemButton>
                    </List>
                </Collapse>
            </List>
        );
    };

    const accountPanel = () => (
        <Collapse in={accountOpen} timeout="auto" unmountOnExit id="account-collapse">
            <div style={{ padding: "10px 16px 6px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar>
                        <PersonIcon sx={{ color: "white" }} />
                    </Avatar>
                    <div style={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 700, lineHeight: 1.1, color: "text.primary" }}>
                            {auth.fullName}
                        </Typography>
                    </div>
                </div>
            </div>

            <Divider sx={{ my: 0.5 }} />

            <List component="div" disablePadding dense>
                <ListItemButton sx={itemSx(false)}>
                    <ListItemIcon sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: "text.secondary" }}>
                        <PersonIcon />
                    </ListItemIcon>
                    <ListItemText primary="Profile" slotProps={{ primary: { variant: "body2", fontWeight: 600 } }} />
                </ListItemButton>

                <ListItemButton sx={itemSx(false)} onClick={() => navigate("/organisation/settings")}>
                    <ListItemIcon sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: "text.secondary" }}>
                        <SettingsIcon />
                    </ListItemIcon>
                    <ListItemText primary="Settings" slotProps={{ primary: { variant: "body2", fontWeight: 600 } }} />
                </ListItemButton>

                <Divider sx={{ my: 0.5 }} />

                <ListItemButton sx={itemSx(false)} onClick={() => navigate("/help")}>
                    <ListItemIcon sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: "text.secondary" }}>
                        <HelpIcon />
                    </ListItemIcon>
                    <ListItemText primary="Help & Support" slotProps={{ primary: { variant: "body2", fontWeight: 600 } }} />
                </ListItemButton>

                <ListItemButton sx={itemSx(false)} onClick={() => navigate("/whatsnew")}>
                    <ListItemIcon sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: "text.secondary" }}>
                        <NewReleasesIcon />
                    </ListItemIcon>
                    <ListItemText primary="What's New" slotProps={{ primary: { variant: "body2", fontWeight: 600 } }} />
                </ListItemButton>

                <Divider sx={{ my: 0.5 }} />

                <ListItemButton
                    onClick={() => auth.logout()}
                    aria-label="Log out"
                    sx={{
                        ...itemSx(false),
                        "& .MuiListItemIcon-root": { color: "error.main" },
                        "& .MuiListItemText-primary": { color: "error.main" }
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 36, "& svg": { fontSize: 22 } }}>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary="Log out" slotProps={{ primary: { variant: "body1", fontWeight: 700 } }} />
                </ListItemButton>
            </List>
        </Collapse>
    );

    const bottomPadding = accountOpen ? 320 : 120;
    const dashHL = matchesFilter("Dashboard");
    const integHL = matchesFilter("Integrations");
    const orgSetHL = matchesFilter("Organization settings");

    return (
        <div
            style={{
                background: "#FFFFFF",
                boxShadow: CARD_SHADOW,
                borderRadius: 16,
                height: "100%",
                width: "clamp(280px, 20vw, 340px)",
                minWidth: "280px",
                display: "flex",
                flexDirection: "column",
                margin: 0,
                padding: 0,
                overflow: "hidden",
                position: "relative"
            }}
        >
            <div style={{padding: "40px 10px 20px 20px"}}>
                <img src={TipCounterLogo} height={"30px"} alt="TipCounter"/>
            </div>

            <Search sx={{ margin: "8px 16px 0px 16px" }} onChange={(input) => setFilter(input ?? "")} />

            <div
                style={{
                    width: "100%",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto",
                    paddingBottom: bottomPadding
                }}
            >
                <List sx={{ mt: "8px" }} dense>
                    <ListItemButton sx={itemSx(false, dashHL)} onClick={() => navigate("/dashboard")}>
                        <ListItemIcon sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: ACCENT }}>
                            <HomeIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="Dashboard"
                            slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                        />
                    </ListItemButton>

                    {organizationMenu()}
                    {countingMenu()}
                    {financeMenu()}

                    <Divider sx={{ my: 0.5 }} />

                    <ListItemButton sx={itemSx(false, integHL)} onClick={() => navigate("/integrations")}>
                        <ListItemIcon sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: ACCENT }}>
                            <ExtensionIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="Integrations"
                            slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                        />
                    </ListItemButton>

                    <ListItemButton sx={itemSx(false, orgSetHL)} onClick={() => navigate("/organisation/settings")}>
                        <ListItemIcon sx={{ minWidth: 36, "& svg": { fontSize: 22 }, color: ACCENT }}>
                            <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText
                            primary="Organization settings"
                            slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                        />
                    </ListItemButton>
                </List>
            </div>

            <div
                ref={accountFooterRef}
                style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "#FFFFFF",
                    borderTop: "1px solid rgba(0,0,0,0.06)",
                    boxShadow: "0 -4px 12px rgba(0,0,0,0.04)",
                    padding: "8px 0px"
                }}
            >
                <List dense sx={{ m: 0, p: 0 }}>
                    {accountPanel()}

                    <ListItemButton
                        aria-controls="account-collapse"
                        aria-expanded={accountOpen ? "true" : "false"}
                        aria-label="Open account menu"
                        onClick={() => setAccountOpen(!accountOpen)}
                        sx={itemSx(false)}
                    >
                        <ListItemAvatar>
                            <Avatar>
                                <PersonIcon sx={{ color: "white" }} />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={auth.fullName}
                            slotProps={{ primary: { component: "span", variant: "body2", fontWeight: 600, noWrap: true } }}
                        />
                        {accountOpen ? <ExpandLess sx={{ color: "text.secondary" }} /> : <ExpandMore sx={{ color: "text.secondary" }} />}
                    </ListItemButton>
                </List>
            </div>
        </div>
    );
}
